import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

// Cookieless session id — random per-browser, persisted so multi-page
// visits collapse into a single "visitor" for the analytics dashboard.
function ensureSessionId() {
  if (typeof window === "undefined") return "ssr";
  try {
    let sid = localStorage.getItem("fl_sid");
    if (!sid) {
      sid = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      localStorage.setItem("fl_sid", sid);
    }
    return sid;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Site-wide page-view tracker. Mounted once at the root of <App />, fires
 * a fire-and-forget POST /api/track/pageview on every route change.
 * Uses raw axios (not our auth-aware api client) so anonymous traffic
 * also lands in the database, but it forwards the Bearer token if the
 * user is logged in so we can attribute the view for funnel analysis.
 */
export function PageViewTracker() {
  const loc = useLocation();
  const lastSent = useRef(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Don't double-count when other state changes (search params, hash)
    const path = loc.pathname;
    if (path.startsWith("/audit/") || document.referrer.includes("/audit/")) return;
    if (lastSent.current === path) return;
    lastSent.current = path;
    // Skip tracking for clearly internal/asset paths (paranoia)
    if (path.startsWith("/api/") || path.startsWith("/static/")) return;
    // GA4 SPA page_view (gtag only exists when REACT_APP_GA4_ID is configured)
    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", "page_view", {
          page_path: path,
          page_location: window.location.href,
          page_title: document.title,
        });
      }
    } catch {}
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/track/pageview`;
    const body = {
      path,
      referrer: document.referrer || null,
      session_id: ensureSessionId(),
      screen_w: window.innerWidth,
      screen_h: window.innerHeight,
    };
    const token = localStorage.getItem("token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    // Use sendBeacon when possible so navigation doesn't drop the request
    try {
      if (navigator.sendBeacon && !token) {
        const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
        navigator.sendBeacon(url, blob);
        return;
      }
    } catch {}
    axios.post(url, body, { headers, timeout: 4000 }).catch(() => {});
  }, [loc.pathname]);
  return null;
}

export default PageViewTracker;
